// Content for the three Trade vertical pages (TIP-495), ported from the
// approved "Membership" design. Each CTA on these pages takes the user to the
// callback form at the bottom of the page (see TradeVerticalPage / CallbackForm).
//
// `accent` keys into TONES (below). The "How can we help?" dropdown options live
// in `callbackOptions`; an explicit "Other" is appended so the form can reveal a
// free-text note. `product` matches the backend CallbackRequest.product choices.

export const TONES = {
  orange: { eyebrow: "#C53A1C", bg: "#FDECE7", text: "#C53A1C" },
  blue: { eyebrow: "#0173C6", bg: "#E3F2FF", text: "#0173C6" },
  green: { eyebrow: "#1B7A45", bg: "#E1F1E8", text: "#1B7A45" },
  gold: { eyebrow: "#9A6B12", bg: "#F6ECD2", text: "#9A6B12" },
};

export const OTHER_OPTION = "Other";

const withOther = (options) => [...options, OTHER_OPTION];

export const TRADE_VERTICALS = {
  information: {
    product: "information",
    accent: "blue",
    eyebrow: "Trade Information",
    headline: "Know who you are trading with.",
    sub: "Know who you are trading with, and prove who you are. Pull an objective profile or a full credit report on any UAE business, or commission an underwriting-grade report on your own. Buy what you need, when you need it. No subscription required.",
    groups: [
      {
        kicker: "Pay as you go",
        title: "Profiles and reports",
        cards: [
          {
            feature: true,
            tag: "Trophy product",
            tone: "gold",
            price: "AED 5,000 – 25,000",
            memberRate: "Members save 10%",
            cta: "Talk to our team",
            ctaKind: "talk",
            micro: "5–30 days · 50/50 payment.",
            title: "Self-Assessment Report",
            body: "An underwriting-grade business report, white-labelled to your brand and built by qualified TREVEX analysts. Submit it to banks, insurers, and large buyers under your own name. Priced by company size, never more than 0.5% of turnover. Includes 12 months of Membership.",
          },
          {
            tag: "Transactional",
            price: "AED 99 / 30-day access",
            memberRate: "Members AED 89.10 · save 10%",
            cta: "Buy a profile",
            ctaKind: "buy",
            ctaAction: "search",
            micro: "",
            title: "Web Profile",
            body: "Objective data on any UAE business, pulled from public registries and platform-claimed records. Any update the business publishes inside your 30-day window is reflected at no extra cost.",
          },
          {
            tag: "Transactional",
            price: "AED 149 / one-time PDF",
            memberRate: "Members AED 134.10 · save 10%",
            cta: "Run a report",
            ctaKind: "buy",
            ctaAction: "form",
            micro: "Get in touch with us.",
            title: "Credit Report",
            body: "Everything in the Web Profile, plus the analytical layer credit teams actually use: payment behaviour, financial trends, and exposure signals. Delivered as a PDF you keep.",
          },
        ],
      },
      {
        kicker: "Member benefits",
        title: "Included with Membership",
        intro: "These two come with Membership. If you are already a Member, they are live on your account and open straight from your dashboard. If not, the panel below shows how to add them.",
        cards: [
          {
            tag: "Member benefit",
            tone: "gold",
            price: "Included with Membership",
            cta: "Go to Partner Program",
            ctaKind: "member",
            micro: "Open from your dashboard",
            title: "Partner Program",
            body: "Submit your buyer ledger. We handle the outreach, onboard every buyer, and run them through Verification. Your dashboard then shows how each buyer pays across the network, with early signals when behaviour shifts.",
          },
          {
            tag: "Member benefit",
            tone: "gold",
            price: "Included with Membership",
            cta: "Share your profile by email",
            ctaKind: "member",
            micro: "Sends an endorsement request",
            title: "Payment Endorsements",
            body: "Collect short confirmations from the credit managers who know you pay. Share your profile with a named individual by email, and their endorsement appears on your profile and nowhere else. The supplier does not need to be on TREVEX.",
          },
        ],
      },
    ],
    nonMember: {
      heading: "Not a Member yet?",
      body: "You can buy any product above without a subscription. Membership lowers the price and opens up more: a flat 10% Member rate on the Web Profile, the Credit Report, the Self-Assessment Report, and Credit Policy Advisory, plus the Partner Program and Payment Endorsements. See the full picture on the Membership page.",
    },
    callbackOptions: withOther([
      "The Self-Assessment Report",
      "Credit Reports at volume",
      "Partner Program or Endorsements",
      "Something else",
    ]),
  },

  finance: {
    product: "finance",
    accent: "orange",
    eyebrow: "Trade Finance",
    headline: "Working capital, when you need it.",
    sub: "Raise a single request and we match you to the right partner. TREVEX works directly with a network of trade finance providers, banks and specialist lenders, and refers you to the one best suited to your requirement. You apply once, through a platform that already holds your data, and there is no cost from TREVEX to make the introduction.",
    groups: [
      {
        kicker: "Our finance network",
        title: "Matched to the right partner",
        cards: [
          {
            title: "Trade Finance Referral",
            tag: "Referral",
            price: "No fee to you",
            cta: "Talk to our team",
            ctaKind: "talk",
            micro: "Direct partners.",
            body: "Working capital and business loans, matched to the partner most likely to fund you. You raise the request, we assess your requirement against the trade finance partners we work with directly, and refer you to the best fit. From there you deal with the lender. TREVEX charges nothing for the introduction. You pay only the rate your chosen partner quotes, with no marketplace fee on top.",
          },
        ],
      },
    ],
    note: {
      title: "No marketplace fee, no middle layer.",
      body: "Some platforms route your request through a marketplace that charges you when a lender funds you, on top of the interest you already pay. TREVEX does not. We hold the partner relationships directly, assess your requirement, refer you to the best fit, and charge you nothing for the introduction. One request inside TREVEX, the right partner, and you pay only what your lender quotes.",
    },
    callbackOptions: withOther([
      "A working capital request",
      "A business loan",
      "Supplier or invoice finance",
      "Not sure yet",
    ]),
  },

  risk: {
    product: "risk",
    accent: "green",
    eyebrow: "Trade Risk",
    headline: "When trade gets complicated.",
    sub: "A credit policy that needs building. A receivable worth insuring. An invoice that has aged past patience. Trade Risk is where TREVEX helps you handle the moments that put cash at risk, with the right product and the right partner for each one.",
    groups: [
      {
        kicker: "Three products",
        title: "Protect, insure, recover",
        cards: [
          {
            tag: "Scoped advisory",
            price: "From AED 5,000",
            memberRate: "Members from AED 4,500 · save 10%",
            cta: "Talk to our team",
            ctaKind: "talk",
            micro: "Quote in 48 hours.",
            title: "Credit Policy Advisory",
            body: "We build your credit policy from scratch, or fix the one you have, to protect cash flow. Scoped to your business, with a fixed quote within 48 hours of a short scoping call. Most engagements land between AED 5,000 and AED 20,000, delivered in 10 to 30 days.",
          },
          {
            tag: "Insurance referral",
            price: "No TREVEX fee to you",
            cta: "Talk to our team",
            ctaKind: "talk",
            micro: "Powered by NGI.",
            title: "Single-Buyer Credit Insurance",
            body: "Cover for a single named buyer in your receivables book, underwritten by National General Insurance. You apply once through TREVEX, the application flows to NGI, and the policy is issued under NGI paper if accepted. You pay the premium NGI quotes. Nothing to TREVEX.",
          },
          {
            tag: "In-house or partner",
            price: "No win, no fee",
            cta: "Talk to our team",
            ctaKind: "talk",
            micro: "Reviewed case by case.",
            title: "Debt Collection",
            body: "Hand us an aged or disputed receivable and we review the case first. TREVEX holds its own UAE debt collection licence, so not every case goes to a partner. We assess each one and run it the way most likely to recover: in-house by TREVEX, or through a UAE collection partner better placed for that debtor. When we collect it in-house, it is no win, no fee. You pay nothing unless we recover.",
          },
        ],
      },
    ],
    note: {
      title: "How collections works at TREVEX.",
      body: "We hold our own UAE debt collection licence, so not every case goes to a partner. We assess each receivable and run it the way most likely to recover, in-house or through a UAE partner with the right reach. When we collect it ourselves, you pay nothing unless we succeed. With Single-Buyer Credit Insurance, TREVEX is the introduction and routing layer, and you pay only the premium NGI quotes.",
    },
    callbackOptions: withOther([
      "Building a credit policy",
      "Insuring a buyer",
      "Recovering an overdue invoice",
      "Not sure yet",
    ]),
  },
};

// Static copy shared by every vertical's callback band + price-promise footer.
export const CALLBACK_BAND = {
  heading: "Not sure which is right for you?",
  body: "Tell us what you are dealing with. A member of the TREVEX team calls you back, usually the same working day, and points you to the product that fits. No obligation, no pressure.",
  reassurance: "Every price you see here is published. The call is to help you choose, not to negotiate.",
};

export const PRICE_PROMISE =
  "Every price here is published. No request-a-quote walls, no hidden tiers, no negotiation. The price you see is the price you pay. Membership is how you lower it, not a conversation with sales.";
